/**
 * Admin panel — push a new GENESYS point list to the backend without an
 * app/web release. Gated on user.role === "admin" (seeded via backend
 * ADMIN_EMAIL/ADMIN_PASSWORD env vars — see backend/auth.py:seed_admin).
 *
 * This is the root page of the standalone admin app (served at "/" on
 * admin.milalabs.io), not a sub-route — there's nothing else here.
 *
 * Editing model: paste/edit the full points list as JSON (card name -> points)
 * rather than a per-row table, since the list is hundreds of cards. "Load
 * bundled list" seeds the textarea from the version shipped in this build so
 * admins aren't starting from a blank slate.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/ui/PageHeader";
import { Upload, RotateCcw, CheckCircle2 } from "lucide-react";
import {
  apiAdminGetGenesysConfig,
  apiAdminPutGenesysConfig,
} from "../lib/cloudSync";
import { genesysBanlists, FORMAT_RULES } from "../lib/genesysData";

const bundledDefault = () => {
  const v = FORMAT_RULES.defaultVersion;
  const banlist = genesysBanlists[v] || { cards: {}, pointCap: 100, label: v, effectiveDate: "" };
  return {
    version: v,
    label: banlist.label || v,
    effectiveDate: banlist.effectiveDate || "",
    pointCap: banlist.pointCap || 100,
    cardsText: JSON.stringify(banlist.cards || {}, null, 2),
  };
};

const AdminGenesys = () => {
  const [loaded, setLoaded] = useState(false);
  const [live, setLive] = useState(null); // currently-pushed remote config, if any
  const [version, setVersion] = useState("");
  const [label, setLabel] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [pointCap, setPointCap] = useState(100);
  const [cardsText, setCardsText] = useState("{}");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // RequireAdmin (applied at the route level in App.js) guarantees this
    // component never mounts until the admin check has passed.
    (async () => {
      try {
        const data = await apiAdminGetGenesysConfig();
        if (data?.config) {
          const c = data.config;
          setLive(c);
          setVersion(c.version || "");
          setLabel(c.label || "");
          setEffectiveDate(c.effectiveDate || "");
          setPointCap(c.pointCap ?? 100);
          setCardsText(JSON.stringify(c.cards || {}, null, 2));
        } else {
          loadBundled();
        }
      } catch {
        loadBundled();
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const loadBundled = () => {
    const d = bundledDefault();
    setVersion(d.version);
    setLabel(d.label);
    setEffectiveDate(d.effectiveDate);
    setPointCap(d.pointCap);
    setCardsText(d.cardsText);
    setError("");
    setSaved(false);
  };

  const cardCount = useMemo(() => {
    try {
      return Object.keys(JSON.parse(cardsText || "{}")).length;
    } catch {
      return null; // invalid JSON
    }
  }, [cardsText]);

  const onSave = async () => {
    setError("");
    setSaved(false);
    let cards;
    try {
      cards = JSON.parse(cardsText);
      if (typeof cards !== "object" || Array.isArray(cards)) throw new Error("must be an object");
    } catch (e) {
      setError("Cards must be valid JSON: { \"Card Name\": points, ... }");
      return;
    }
    if (!version.trim()) {
      setError("Version is required (e.g. 2026.04).");
      return;
    }
    setBusy(true);
    try {
      const result = await apiAdminPutGenesysConfig({
        version: version.trim(),
        label: label.trim() || version.trim(),
        effectiveDate: effectiveDate.trim() || null,
        pointCap: Number(pointCap) || 100,
        cards,
      });
      setLive(result.config);
      setSaved(true);
    } catch (e) {
      setError(e?.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-genesys-page">
      <PageHeader
        title="GENESYS Point List"
        subtitle="Pushes live to mobile + web on next app launch — no release needed."
      />
      {(live || loaded) && (
        <p className="text-xs text-celestial-muted/70 -mt-4">
          {live ? (
            <>
              Currently live: <span className="text-celestial-text">{live.version}</span>
              {live.updated_by && <> — last updated by {live.updated_by}</>}
            </>
          ) : (
            "No override pushed yet — apps are using their bundled default list."
          )}
        </p>
      )}

      <div>
        <div className="card-celestial rounded-2xl p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Version" value={version} onChange={setVersion} placeholder="2026.04" />
            <Field label="Point cap" value={pointCap} onChange={setPointCap} type="number" />
            <Field label="Label" value={label} onChange={setLabel} placeholder="April 2026" />
            <Field label="Effective date" value={effectiveDate} onChange={setEffectiveDate} placeholder="2026-04-01" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-celestial-muted">
                Cards (JSON — card name → points){cardCount !== null && <span className="ml-2 text-celestial-text">{cardCount} cards</span>}
              </label>
              <button
                type="button"
                onClick={loadBundled}
                className="text-xs text-glow-light hover:text-glow-mid flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Load bundled list
              </button>
            </div>
            <textarea
              value={cardsText}
              onChange={(e) => setCardsText(e.target.value)}
              rows={16}
              spellCheck={false}
              className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text font-mono text-xs focus:outline-none focus:border-glow-mid/50"
              data-testid="admin-genesys-cards-json"
            />
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{error}</div>
          )}
          {saved && !error && (
            <div className="text-sm text-success bg-success/10 border border-success/30 rounded-xl px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Live — apps will pick this up on next launch.
            </div>
          )}

          <Button
            onClick={onSave}
            disabled={busy}
            className="w-full btn-celestial rounded-xl py-2.5 flex items-center justify-center gap-2"
            data-testid="admin-genesys-save"
          >
            <Upload className="w-4 h-4" /> {busy ? "Pushing…" : "Push live"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", placeholder }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-celestial-muted">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50"
    />
  </div>
);

export default AdminGenesys;
